'use client';

import './styles.css';

import { AudioPath } from '@/utils/audio';

import { IconButton } from '@/components/atoms/iconButton';
import { Text } from '@/components/atoms/text';

import { durationAsMinutesSeconds } from '@/decodo/components/atoms/Audio/utils';
import { useMediaPlayback } from '@/hooks/useMediaPlayback';
import PauseIcon from '~/svg/audio-player/IconPause.svg';
import PauseIconActive from '~/svg/audio-player/IconPauseActive.svg';
import PlayIcon from '~/svg/audio-player/IconPlay.svg';
import PlayIconActive from '~/svg/audio-player/IconPlayActive.svg';
import { durationToPercentage } from './utils';

const mutualIconStyle = 'stroke-gray-950 h-8 w-8 md:h-6 md:w-6';

export type AudioProps = {
  audioTrack: AudioPath;
};

const Audio = ({ audioTrack }: AudioProps) => {
  const {
    isPlaying,
    progress,
    duration,
    mediaRef,
    timelineRef,
    handlePlaybackComplete,
    togglePlay,
    handleMount,
    handleUpdateTime,
    handleChangeHeadPosition,
  } = useMediaPlayback<HTMLAudioElement>();

  const { minutes: durationMinutes, seconds: durationSeconds } =
    durationAsMinutesSeconds(duration);
  const { minutes: progressMinutes, seconds: progressSeconds } =
    durationAsMinutesSeconds(progress);

  return (
    <div className='mt-6 w-full min-w-40 rounded-xl bg-gray-100 px-2'>
      <audio
        onLoadedMetadata={handleMount}
        onTimeUpdate={handleUpdateTime}
        onEnded={handlePlaybackComplete}
        ref={mediaRef}
        src={audioTrack}
      />
      <div className='flex w-full flex-row'>
        <IconButton
          aria-description='Play/Pause'
          className='mx-2 my-2 p-0'
          onClick={togglePlay}
          data-tracker-component='audio-play-pause-button'
        >
          <div className='hover-playback-button h-8 w-8 md:h-6 md:w-6'>
            {/* NOTE: these icons are hover-conditioned. See the ./styles.css file for details. */}
            {!isPlaying ? (
              <>
                <PlayIcon
                  className={mutualIconStyle}
                  data-tracker-component='audio-play-icon'
                />
                <PlayIconActive
                  className={mutualIconStyle}
                  data-tracker-component='audio-play-icon-active'
                />
              </>
            ) : (
              <>
                <PauseIcon
                  className={mutualIconStyle}
                  data-tracker-component='audio-pause-icon'
                />
                <PauseIconActive
                  className={mutualIconStyle}
                  data-tracker-component='audio-pause-icon-active'
                />
              </>
            )}
          </div>
        </IconButton>

        <div className='relative mx-2 w-full pt-2'>
          <div className='flex justify-between'>
            <Text
              data-tracker-component='audio-progress'
              color='gray-500'
              variant='body-s'
              classNames={{ wrapper: 'font-serif' }}
            >
              {progressMinutes}:{progressSeconds}
            </Text>
            <Text
              data-tracker-component='audio-duration'
              color='gray-400'
              variant='body-s'
              classNames={{ wrapper: 'font-serif' }}
            >
              {durationMinutes}:{durationSeconds}
            </Text>
          </div>
          <input
            onChange={handleChangeHeadPosition}
            ref={timelineRef}
            type='range'
            className='audio-playback-slider absolute top-6'
            max='100'
            data-tracker-component='audio-seek-slider'
            value={durationToPercentage({
              currentTime: progress,
              duration: duration,
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default Audio;
